import React, { useEffect, useState, useMemo } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  message,
  Card,
  Row,
  Col,
  Statistic,
  Tag,
  Space,
  Tooltip,
} from 'antd';
import {
  PlusOutlined,
  UploadOutlined,
  DeleteOutlined,
  EditOutlined,
  HolderOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import {
  fetchBannerList,
  createBanner,
  deleteBanner,
  uploadBannerImage,
  updateBannerGroup,
  reorderBanners,
} from '@/api/banner';
import { getImageUrl, handleImageError } from '../../utils/image';

const groupByTitle = list => {
  const map = new Map();
  list.forEach(b => {
    const t = (b.title || '未命名').trim();
    if (!map.has(t)) map.set(t, []);
    map.get(t).push(b);
  });
  for (const arr of map.values()) arr.sort((a, b) => Number(a.sort_order) - Number(b.sort_order) || new Date(a.created_at) - new Date(b.created_at));
  return Array.from(map.entries())
    .map(([title, items]) => ({ title, items, minSort: Math.min(...items.map(i => Number(i.sort_order) || 0)), count: items.length }))
    .sort((a, b) => a.minSort - b.minSort || a.title.localeCompare(b.title));
};

const BannerList = () => {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingTitle, setEditingTitle] = useState(null);
  const [form] = Form.useForm();
  const [uploading, setUploading] = useState(false);
  const [groupItems, setGroupItems] = useState([]);
  const [savingOrder, setSavingOrder] = useState(false);
  const [dragTitle, setDragTitle] = useState(null);
  const [dragItemKey, setDragItemKey] = useState(null);

  const groups = useMemo(() => groupByTitle(data), [data]);
  const activeCount = data.filter(item => Number(item.status) === 1).length;
  const missingImageCount = data.filter(item => !item.image_url).length;

  const fetchData = async (p = 1, ps = 20) => {
    setLoading(true);
    try {
      const res = await fetchBannerList({ page: p, pageSize: ps });
      const list = res.data.list || res.data.data?.list || [];
      const tot = res.data.pagination?.total ?? res.data.total ?? res.data.data?.total ?? list.length;
      setData(list);
      setTotal(tot);
    } catch (e) {
      message.error('获取数据失败');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData(page, pageSize);
  }, [page, pageSize]);

  const openAdd = () => {
    setEditingTitle(null);
    setGroupItems([{ key: `new-${Date.now()}`, id: null, file: null, previewUrl: '', imageUrl: '', link_url: '', sort_order: total + 1 }]);
    form.resetFields();
    form.setFieldsValue({ title: '', sort_order: total + 1, status: true });
    setModalVisible(true);
  };

  const openEditGroup = group => {
    setEditingTitle(group.title);
    const items = group.items.map((b, idx) => ({
      key: `id-${b.id}`,
      id: b.id,
      file: null,
      previewUrl: '',
      imageUrl: b.image_url,
      link_url: b.link_url || '',
      sort_order: Number(b.sort_order) || group.minSort + idx,
    }));
    setGroupItems(items);
    form.setFieldsValue({ title: group.title, sort_order: group.minSort, status: group.items.every(x => Number(x.status) === 1) });
    setModalVisible(true);
  };

  const handleGroupItemFile = (key, e) => {
    const f = e.target.files[0];
    if (f && f.size > 20 * 1024 * 1024) {
      message.error('图片不能超过20MB');
      e.target.value = '';
      return;
    }
    setGroupItems(prev => prev.map(it => {
      if (it.key !== key) return it;
      if (it.previewUrl) URL.revokeObjectURL(it.previewUrl);
      return { ...it, file: f || null, previewUrl: f ? URL.createObjectURL(f) : '' };
    }));
    e.target.value = '';
  };

  const addGroupItem = () => {
    setGroupItems(prev => [...prev, { key: `new-${Date.now()}-${Math.random()}`, id: null, file: null, previewUrl: '', imageUrl: '', link_url: '', sort_order: (prev[prev.length - 1]?.sort_order || 0) + 1 }]);
  };

  const removeGroupItem = key => {
    setGroupItems(prev => {
      if (prev.length <= 1) {
        message.warning('至少保留一张图片');
        return prev;
      }
      const t = prev.find(x => x.key === key);
      if (t?.previewUrl) URL.revokeObjectURL(t.previewUrl);
      return prev.filter(x => x.key !== key);
    });
  };

  const moveGroupItem = (idx, dir) => {
    const to = idx + dir;
    if (to < 0 || to >= groupItems.length) return;
    setGroupItems(prev => {
      const next = [...prev];
      [next[idx], next[to]] = [next[to], next[idx]];
      return next;
    });
  };

  const handleSaveGroup = async () => {
    try {
      const values = await form.validateFields();
      const title = String(values.title || '').trim();
      if (!title) { message.error('请输入标题'); return; }
      const base = Number(values.sort_order);
      if (!base || base < 1) { message.error('排序号必须大于0'); return; }
      const hasImage = groupItems.some(it => it.file || it.imageUrl);
      if (!hasImage) { message.error('至少添加一张图片'); return; }
      setUploading(true);
      const uploaded = [];
      for (let i = 0; i < groupItems.length; i++) {
        const it = groupItems[i];
        if (!it.file && !it.imageUrl) continue;
        let url = it.imageUrl;
        if (it.file) {
          const fd = new FormData();
          fd.append('file', it.file);
          const res = await uploadBannerImage(fd);
          url = res?.data?.url || res?.data?.imageUrl || res?.url;
          if (!(res?.success || res?.code === 0 || res?.code === 200) || !url) throw new Error(res?.message || res?.msg || `第${i + 1}张上传失败`);
        }
        uploaded.push({ id: it.id || undefined, image_url: url, link_url: it.link_url || null, sort_order: base + i });
      }
      if (editingTitle != null) {
        await updateBannerGroup(editingTitle, { title, sort_order: base, status: values.status ? 1 : 0, items: uploaded });
        message.success(`已更新「${title}」${uploaded.length}张`);
      } else {
        if (uploaded.length === 1) {
          await createBanner({ title, image_url: uploaded[0].image_url, link_url: uploaded[0].link_url, sort_order: base, status: values.status ? 1 : 0 });
          message.success('轮播图创建成功');
        } else {
          const res = await createBanner({ title, sort_order: base, status: values.status ? 1 : 0, items: uploaded.map(x => ({ image_url: x.image_url, link_url: x.link_url })) });
          message.success(res?.message || `已批量创建${uploaded.length}张`);
        }
      }
      setModalVisible(false);
      setEditingTitle(null);
      setGroupItems([]);
      await fetchData(page, pageSize);
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.message || '保存失败');
    }
    setUploading(false);
  };

  const handleDeleteGroup = group => {
    Modal.confirm({
      title: `确认删除分组「${group.title}」的${group.items.length}张轮播图？`,
      okText: '删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        for (const b of group.items) await deleteBanner(b.id);
        message.success('分组已删除');
        fetchData(page, pageSize);
      },
    });
  };

  const buildGlobalOrders = (nextGroups) => {
    const orders = [];
    let cursor = 1;
    for (const g of nextGroups) {
      for (const b of g.items) {
        orders.push({ id: b.id, sort_order: cursor++ });
      }
    }
    return orders;
  };

  const handleGroupDragStart = (title) => setDragTitle(title);
  const handleGroupDrop = async (targetTitle) => {
    if (!dragTitle || dragTitle === targetTitle) { setDragTitle(null); return; }
    const fromIdx = groups.findIndex(g => g.title === dragTitle);
    const toIdx = groups.findIndex(g => g.title === targetTitle);
    if (fromIdx < 0 || toIdx < 0) { setDragTitle(null); return; }
    const next = [...groups];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    const orders = buildGlobalOrders(next);
    setData(prev => {
      const map = new Map(orders.map(o => [o.id, o.sort_order]));
      return prev.map(b => map.has(b.id) ? { ...b, sort_order: map.get(b.id) } : b).sort((a, b) => a.sort_order - b.sort_order);
    });
    setDragTitle(null);
    try {
      setSavingOrder(true);
      await reorderBanners(orders);
      message.success('分组顺序已保存，前端已刷新');
      await fetchData(page, pageSize);
    } catch (e) { message.error(e?.message || '保存排序失败'); }
    setSavingOrder(false);
  };

  const handleItemDragStart = (key) => setDragItemKey(key);
  const handleItemDrop = async (group, targetKey) => {
    if (!dragItemKey || dragItemKey === targetKey) { setDragItemKey(null); return; }
    const fromIdx = group.items.findIndex(x => `id-${x.id}` === dragItemKey);
    const toIdx = group.items.findIndex(x => `id-${x.id}` === targetKey);
    if (fromIdx < 0 || toIdx < 0) { setDragItemKey(null); return; }
    const reorderedGroups = groups.map(g => {
      if (g.title !== group.title) return g;
      const items = [...g.items];
      const [mv] = items.splice(fromIdx, 1);
      items.splice(toIdx, 0, mv);
      return { ...g, items };
    });
    const orders = buildGlobalOrders(reorderedGroups);
    setData(prev => {
      const map = new Map(orders.map(o => [o.id, o.sort_order]));
      return prev.map(b => map.has(b.id) ? { ...b, sort_order: map.get(b.id) } : b).sort((a, b) => a.sort_order - b.sort_order);
    });
    setDragItemKey(null);
    try {
      setSavingOrder(true);
      await reorderBanners(orders);
      message.success('组内顺序已保存');
      await fetchData(page, pageSize);
    } catch (e) { message.error(e?.message || '保存失败'); }
    setSavingOrder(false);
  };

  const handleInlineSortChange = async (group, newBase) => {
    if (!newBase || newBase < 1) return;
    const nextGroups = groups.map(g => g.title === group.title ? g : g);
    const target = nextGroups.find(g => g.title === group.title);
    const without = nextGroups.filter(g => g.title !== group.title);
    without.push(target);
    without.sort((a, b) => {
      if (a.title === group.title) return newBase - b.minSort;
      if (b.title === group.title) return a.minSort - newBase;
      return a.minSort - b.minSort;
    });
    const idx = without.findIndex(g => g.title === group.title);
    const reordered = [...without];
    const [mv] = reordered.splice(idx, 1);
    reordered.splice(0, 0, mv);
    reordered.sort((a, b) => {
      const ai = reordered.indexOf(a);
      const bi = reordered.indexOf(b);
      if (a.title === group.title) return -1;
      if (b.title === group.title) return 1;
      return ai - bi;
    });
    // 简化：直接按输入值重算该组起始，其余顺延
    const ordered = [...groups].sort((a, b) => a.minSort - b.minSort);
    const pos = ordered.findIndex(g => g.title === group.title);
    const before = ordered.slice(0, pos);
    const after = ordered.slice(pos + 1);
    const rebuilt = [...before, group, ...after];
    let cursor = 1;
    const orders = [];
    for (const g of rebuilt) {
      if (g.title === group.title) {
        for (let i = 0; i < g.items.length; i++) orders.push({ id: g.items[i].id, sort_order: newBase + i });
        cursor = newBase + g.items.length;
      } else {
        for (const b of g.items) {
          if (Number(b.sort_order) >= newBase) orders.push({ id: b.id, sort_order: cursor >= newBase + group.items.length ? cursor : Number(b.sort_order) });
          else orders.push({ id: b.id, sort_order: Number(b.sort_order) });
        }
      }
    }
    const cleanOrders = buildGlobalOrders(rebuilt.map(g => g.title === group.title ? { ...g, items: g.items.map((b, i) => ({ ...b, sort_order: newBase + i })) } : g).sort((a, b) => Math.min(...a.items.map(x => x.sort_order)) - Math.min(...b.items.map(x => x.sort_order))));
    try {
      setSavingOrder(true);
      await reorderBanners(cleanOrders);
      message.success('排序已更新');
      await fetchData(page, pageSize);
    } catch (e) { message.error(e?.message || '更新失败'); }
    setSavingOrder(false);
  };

  return (
    <div className="workspace-page">
      <div className="workspace-header">
        <div>
          <h1 className="workspace-title">轮播图管理</h1>
          <p className="workspace-description">按标题分组管理；同一标题的多张图可在同一弹窗增删改；支持拖拽与排序号直接修改，组间/组内均可</p>
        </div>
        <Space>
          <Button icon={<SaveOutlined />} loading={savingOrder} onClick={() => fetchData(page, pageSize)}>刷新</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>新增轮播组</Button>
        </Space>
      </div>

      <Row gutter={[16, 16]} className="workspace-summary">
        <Col xs={12} md={6}><Card size="small"><Statistic title="全部轮播图" value={total} /></Card></Col>
        <Col xs={12} md={6}><Card size="small"><Statistic title="分组数" value={groups.length} /></Card></Col>
        <Col xs={12} md={6}><Card size="small"><Statistic title="当前页已启用" value={activeCount} /></Card></Col>
        <Col xs={12} md={6}><Card size="small"><Statistic title="图片异常" value={missingImageCount} valueStyle={{ color: missingImageCount ? '#c4473d' : '#23855b' }} /></Card></Col>
      </Row>

      <Card className="workspace-data-card" loading={loading}>
        <div className="workspace-section-heading workspace-section-heading-padded">
          <div>
            <div className="workspace-section-title">首页展示内容（按标题分组，上下拖拽可调换组顺序）</div>
            <div className="workspace-section-description">组内可拖拽缩略图调换同一标题下图片顺序；亦可在弹窗内增/删/替换任意张，或在分组卡片上直接改起始排序号</div>
          </div>
          <span className="workspace-result-count">共 {total} 张 / {groups.length} 组</span>
        </div>

        {groups.length === 0 && !loading ? <div style={{ padding: 40, textAlign: 'center', color: '#999' }}>暂无轮播图，点击右上角新增轮播组</div> : null}

        <Space direction="vertical" style={{ width: '100%' }} size={16}>
          {groups.map(group => (
            <Card
              key={group.title}
              size="small"
              draggable
              onDragStart={() => handleGroupDragStart(group.title)}
              onDragOver={e => e.preventDefault()}
              onDrop={() => handleGroupDrop(group.title)}
              style={{ borderLeft: dragTitle === group.title ? '3px solid #1677ff' : undefined, opacity: dragTitle === group.title ? 0.6 : 1 }}
              title={
                <Space>
                  <HolderOutlined style={{ cursor: 'grab', color: '#999' }} title="拖拽整组以调换不同标题的顺序" />
                  <span style={{ fontWeight: 700 }}>{group.title}</span>
                  <Tag>{group.count}张</Tag>
                  <Tag color={group.items.every(x => Number(x.status) === 1) ? 'green' : 'default'}>{group.items.every(x => Number(x.status) === 1) ? '启用' : '部分禁用'}</Tag>
                  <span style={{ fontSize: 12, color: '#999' }}>起始排序 {group.minSort}</span>
                </Space>
              }
              extra={
                <Space>
                  <Tooltip title="直接输入新的起始排序号，回车即可全局重排">
                    <InputNumber size="small" min={1} defaultValue={group.minSort} style={{ width: 90 }} onPressEnter={e => handleInlineSortChange(group, Number(e.target.value))} onStep={(v) => handleInlineSortChange(group, Number(v))} />
                  </Tooltip>
                  <Button size="small" icon={<EditOutlined />} onClick={() => openEditGroup(group)}>编辑整组</Button>
                  <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDeleteGroup(group)}>删整组</Button>
                </Space>
              }
            >
              <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 8 }}>
                {group.items.map((item, idx) => (
                  <div
                    key={item.id}
                    draggable
                    onDragStart={() => handleItemDragStart(`id-${item.id}`)}
                    onDragOver={e => e.preventDefault()}
                    onDrop={() => handleItemDrop(group, `id-${item.id}`)}
                    style={{ minWidth: 110, textAlign: 'center', border: dragItemKey === `id-${item.id}` ? '1px dashed #1677ff' : '1px solid #f0f0f0', borderRadius: 8, padding: 6, background: '#fafafa' }}
                  >
                    <img src={getImageUrl(item.image_url)} alt="" style={{ width: 96, height: 64, objectFit: 'cover', borderRadius: 6, display: 'block', margin: '0 auto 6px' }} onError={handleImageError} />
                    <div style={{ fontSize: 11, color: '#999' }}>#{item.sort_order} <HolderOutlined title="拖拽调换同标题下顺序" /></div>
                    <div style={{ fontSize: 11, color: '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 96 }} title={item.link_url || '无跳转'}>{item.link_url || '无跳转'}</div>
                    <Space size={2} style={{ marginTop: 4 }}>
                      <Button size="small" type="text" disabled={idx === 0} icon={<ArrowUpOutlined />} onClick={() => {
                        const orders = buildGlobalOrders(groups.map(g => g.title === group.title ? { ...g, items: (() => { const a = [...g.items]; [a[idx], a[idx - 1]] = [a[idx - 1], a[idx]]; return a; })() } : g));
                        reorderBanners(orders).then(() => fetchData(page, pageSize));
                      }} />
                      <Button size="small" type="text" disabled={idx === group.items.length - 1} icon={<ArrowDownOutlined />} onClick={() => {
                        const orders = buildGlobalOrders(groups.map(g => g.title === group.title ? { ...g, items: (() => { const a = [...g.items]; [a[idx], a[idx + 1]] = [a[idx + 1], a[idx]]; return a; })() } : g));
                        reorderBanners(orders).then(() => fetchData(page, pageSize));
                      }} />
                    </Space>
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </Space>

        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
          <Table
            rowKey="id"
            dataSource={data}
            loading={loading}
            size="small"
            pagination={{ current: page, pageSize, total, showSizeChanger: true, onChange: (p, ps) => { setPage(p); setPageSize(ps); } }}
            columns={[
              { title: 'ID', dataIndex: 'id', width: 70 },
              { title: '标题', dataIndex: 'title' },
              { title: '排序', dataIndex: 'sort_order', width: 80 },
              { title: '状态', dataIndex: 'status', width: 80, render: v => v === 1 ? <Tag color="green">启用</Tag> : <Tag>禁用</Tag> },
            ]}
            style={{ width: '100%' }}
          />
        </div>
      </Card>

      <Modal
        title={editingTitle != null ? `编辑分组：${editingTitle}` : '新增轮播组（同一标题可多张）'}
        open={modalVisible}
        footer={null}
        width={720}
        onCancel={() => { setModalVisible(false); setEditingTitle(null); }}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onValuesChange={() => {}}>
          <Form.Item label="标题" name="title" rules={[{ required: true, message: '请输入同一标题下共享的标题' }]} extra="同一标题的所有图片会按排序连续轮播；修改标题即整组改名">
            <Input placeholder="例如：盛世庆团圆，奔马跃锦程" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="起始排序" name="sort_order" rules={[{ required: true, message: '请输入起始排序' }, { type: 'number', min: 1, message: '必须大于0' }]} extra="组内多张会以此为起点依次递增">
                <InputNumber min={1} style={{ width: '100%' }} placeholder="例如 1" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="状态" name="status" valuePropName="checked" extra="整组统一启用/禁用">
                <Switch checkedChildren="启用" unCheckedChildren="禁用" />
              </Form.Item>
            </Col>
            <Col span={8} style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 24 }}>
              <Button icon={<PlusOutlined />} onClick={addGroupItem}>新增一张</Button>
            </Col>
          </Row>

          <div style={{ marginBottom: 8, fontWeight: 600 }}>组内图片（{groupItems.length}张）—— 拖拽或用箭头调换同标题顺序，可追加新图</div>
          <Space direction="vertical" style={{ width: '100%' }} size={8}>
            {groupItems.map((it, idx) => (
              <Card key={it.key} size="small" style={{ background: '#fafafa' }} title={`第 ${idx + 1} 张 ${it.id ? `#${it.id}` : '(新)'}`} extra={<Space><Button size="small" disabled={idx === 0} onClick={() => moveGroupItem(idx, -1)}>↑</Button><Button size="small" disabled={idx === groupItems.length - 1} onClick={() => moveGroupItem(idx, 1)}>↓</Button><Button size="small" danger icon={<DeleteOutlined />} onClick={() => removeGroupItem(it.key)}>移除</Button></Space>}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  <div>
                    <Button icon={<UploadOutlined />} size="small" onClick={() => document.getElementById(`grp-file-${it.key}`).click()}>{it.file || it.imageUrl ? '更换图片' : '选择图片'}</Button>
                    <input id={`grp-file-${it.key}`} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => handleGroupItemFile(it.key, e)} />
                    {(it.previewUrl && it.file) ? <img src={it.previewUrl} alt="预览" style={{ width: 120, display: 'block', marginTop: 8, borderRadius: 6 }} /> : null}
                    {it.imageUrl && !it.file ? <img src={getImageUrl(it.imageUrl)} alt="当前" style={{ width: 120, display: 'block', marginTop: 8, borderRadius: 6 }} onError={handleImageError} /> : null}
                    {!it.file && !it.imageUrl ? <div style={{ marginTop: 8, fontSize: 12, color: '#c4473d' }}>未选择图片</div> : null}
                    <div style={{ marginTop: 4, fontSize: 11, color: '#999' }}>排序 {Number(form.getFieldValue('sort_order')) + idx || idx + 1}</div>
                  </div>
                  <div style={{ flex: 1, minWidth: 220 }}>
                    <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>点击该图后打开（可留空）</div>
                    <Input placeholder="例如：/pages/product-detail/product-detail?id=1 或留空" value={it.link_url} onChange={e => setGroupItems(prev => prev.map(x => x.key === it.key ? { ...x, link_url: e.target.value } : x))} />
                  </div>
                </div>
              </Card>
            ))}
          </Space>
          <div style={{ fontSize: 12, color: '#999', marginTop: 8 }}>提示：同一标题的多张图共享标题与状态；可在编辑时随意增/删/换图，保存即整组原子更新。</div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
            <Button onClick={() => setModalVisible(false)}>取消</Button>
            <Button type="primary" icon={<SaveOutlined />} loading={uploading} onClick={handleSaveGroup}>保存整组</Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default BannerList;
